import { useEffect, useState, useCallback } from "react";
import { Sparkle, Download, Users, Coins, CircleNotch, CaretRight } from "@phosphor-icons/react";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const RANGES = [
  { d: 7, label: "7 days" },
  { d: 30, label: "30 days" },
  { d: 90, label: "90 days" },
  { d: 365, label: "12 months" },
];

export default function AiUsageReport() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [drill, setDrill] = useState(null); // { email, name } selected user
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const r = await api.get(`/tenant/ai-usage?days=${d}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadBlob(`/tenant/ai-usage.csv?days=${days}`, `ai-usage-${days}d.csv`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExporting(false); }
  };

  const openDrill = async (u) => {
    setDrill(u);
    setDrillData(null);
    setDrillLoading(true);
    try {
      const r = await api.get(`/tenant/ai-usage/user?email=${encodeURIComponent(u.email)}&days=${days}`);
      setDrillData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
      setDrill(null);
    } finally { setDrillLoading(false); }
  };

  return (
    <div data-testid="ai-usage-report">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <p className="text-sm text-muted-foreground">
          AI credit consumption for <span className="font-medium text-foreground">{data?.entity?.name || "your organization"}</span> only — per user and per feature.
        </p>
        <div className="flex items-center gap-2">
          <div className="flex border border-border">
            {RANGES.map((r) => (
              <button key={r.d} type="button" onClick={() => setDays(r.d)} data-testid={`ai-usage-range-${r.d}`}
                className={`text-xs px-3 py-1.5 ${days === r.d ? "bg-primary text-white" : "hover:bg-card"}`}>
                {r.label}
              </button>
            ))}
          </div>
          <Button variant="outline" className="rounded-none" onClick={exportCsv} disabled={exporting || !data?.total_actions}
            data-testid="ai-usage-export">
            {exporting ? <CircleNotch size={16} className="mr-1 animate-spin" /> : <Download size={16} className="mr-1" />}
            Export CSV
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-10 flex items-center gap-2" data-testid="ai-usage-loading">
          <CircleNotch size={16} className="animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {/* summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            <div className="border border-border bg-card p-4" data-testid="ai-usage-total-credits">
              <div className="flex items-center gap-2 overline text-muted-foreground"><Coins size={14} /> Credits used</div>
              <div className="text-3xl font-display font-bold mt-1">{data.total_credits.toLocaleString()}</div>
            </div>
            <div className="border border-border bg-card p-4" data-testid="ai-usage-total-actions">
              <div className="flex items-center gap-2 overline text-muted-foreground"><Sparkle size={14} /> AI actions</div>
              <div className="text-3xl font-display font-bold mt-1">{data.total_actions.toLocaleString()}</div>
            </div>
            <div className="border border-border bg-card p-4" data-testid="ai-usage-user-count">
              <div className="flex items-center gap-2 overline text-muted-foreground"><Users size={14} /> Active users</div>
              <div className="text-3xl font-display font-bold mt-1">{data.user_count.toLocaleString()}</div>
            </div>
          </div>

          {data.total_actions === 0 ? (
            <div className="border border-border bg-card p-10 text-center text-sm text-muted-foreground" data-testid="ai-usage-empty">
              No AI usage recorded in this period.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* by user */}
              <div>
                <h3 className="overline text-muted-foreground mb-2">By user</h3>
                <div className="border border-border bg-card" data-testid="ai-usage-by-user">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-border bg-secondary/40 text-left overline text-muted-foreground">
                      <th className="px-3 py-2">User</th><th className="px-3 py-2 text-right">Actions</th><th className="px-3 py-2 text-right">Credits</th>
                    </tr></thead>
                    <tbody>
                      {data.by_user.map((u) => (
                        <tr key={u.email} onClick={() => openDrill(u)} data-testid="ai-usage-user-row"
                          className="border-b border-border last:border-0 cursor-pointer hover:bg-secondary/40">
                          <td className="px-3 py-2">
                            <div className="font-medium flex items-center gap-1">{u.name || u.email}<CaretRight size={12} className="text-muted-foreground" /></div>
                            <div className="text-xs text-muted-foreground">{u.email}</div>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{u.actions}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium">{u.credits}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* by feature */}
              <div>
                <h3 className="overline text-muted-foreground mb-2">By feature</h3>
                <div className="border border-border bg-card" data-testid="ai-usage-by-feature">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-border bg-secondary/40 text-left overline text-muted-foreground">
                      <th className="px-3 py-2">Feature</th><th className="px-3 py-2 text-right">Actions</th><th className="px-3 py-2 text-right">Credits</th>
                    </tr></thead>
                    <tbody>
                      {data.by_feature.map((f) => (
                        <tr key={f.reason} className="border-b border-border last:border-0" data-testid="ai-usage-feature-row">
                          <td className="px-3 py-2">{f.label}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{f.actions}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium">{f.credits}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <Dialog open={!!drill} onOpenChange={(o) => { if (!o) { setDrill(null); setDrillData(null); } }}>
        <DialogContent className="rounded-none max-w-lg" data-testid="ai-usage-drill">
          <DialogHeader>
            <DialogTitle className="text-base">
              {drill?.name || drill?.email}
              <span className="block text-xs font-normal text-muted-foreground mt-0.5">{drill?.email} · last {days} days</span>
            </DialogTitle>
          </DialogHeader>
          {drillLoading ? (
            <div className="text-sm text-muted-foreground py-8 flex items-center gap-2"><CircleNotch size={16} className="animate-spin" /> Loading…</div>
          ) : drillData ? (
            <>
              <div className="flex gap-6 text-sm border-b border-border pb-3 mb-1">
                <div><span className="font-semibold">{drillData.total_actions}</span> <span className="text-muted-foreground">actions</span></div>
                <div><span className="font-semibold">{drillData.total_credits}</span> <span className="text-muted-foreground">credits</span></div>
              </div>
              <div className="max-h-[55vh] overflow-y-auto -mx-2 px-2" data-testid="ai-usage-drill-list">
                {drillData.actions.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-6 text-center">No AI actions in this period.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead><tr className="text-left overline text-muted-foreground border-b border-border">
                      <th className="py-2">When (UTC)</th><th className="py-2">Feature</th><th className="py-2 text-right">Credits</th>
                    </tr></thead>
                    <tbody>
                      {drillData.actions.map((a, i) => (
                        <tr key={i} className="border-b border-border last:border-0" data-testid="ai-usage-drill-row">
                          <td className="py-2 font-mono text-xs">{(a.at || "").slice(0, 19).replace("T", " ")}</td>
                          <td className="py-2">{a.feature}</td>
                          <td className="py-2 text-right tabular-nums">{a.credits}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
